import { defineStore } from 'pinia';
import { Store } from '@/setup/globals';
import { setInitialData } from '@/stores/plugins/initial-data';
import api from '@/helpers/api';

export type StoreState = {
  entries: Record<string, unknown>[];
  brands: Record<string, unknown>[];
  history: Record<string, unknown>;
  relations: Record<string, unknown>;
  relationParentFile: Record<string, unknown> | null;
  relatedAssetFile: Record<string, unknown> | null;
  detailViewFile: Record<string, never> | null;
  downloadViewFile: Record<string, unknown> | null;
  showAllDetailValues: boolean;
  requestElectronicInstructionItem: Record<string, unknown> | null;
  runningRequests: {
    apiFetchElectronicInstructions: boolean;
    apiFetchHistory: boolean;
    apiFetchBrands: boolean;
    apiRequestHardCopy: boolean;
    apiReportProblem: boolean;
    apiFetchElectronicInstructionByDocumentId: boolean;
    apiSubscribeToAssetVersionUpdates: boolean;
  };
};

const storeName = Store.Eifu;

export default defineStore(storeName, {
  state: () => {
    const initialData: Partial<StoreState> = window.initialData?.[storeName] || {};
    const state: StoreState = {
      entries: [],
      brands: [],
      history: {},
      relations: {},
      relationParentFile: null,
      relatedAssetFile: null,
      detailViewFile: null,
      downloadViewFile: null,
      showAllDetailValues: false,
      requestElectronicInstructionItem: null,
      runningRequests: {
        apiFetchElectronicInstructions: false,
        apiFetchHistory: false,
        apiFetchBrands: false,
        apiRequestHardCopy: false,
        apiReportProblem: false,
        apiFetchElectronicInstructionByDocumentId: false,
        apiSubscribeToAssetVersionUpdates: false,
      },
    };

    setInitialData(state, initialData);

    return state;
  },
  getters: {
    getFlatRelations: (state) => Object.values(state.relations).flat(1),
  },
  actions: {
    /**
     * Fetches a specific electronic instructions document by its id.
     */
    apiFetchElectronicInstructionByDocumentId(documentId) {
      this.runningRequests.apiFetchElectronicInstructionByDocumentId = true;

      const url = api.getUrl('eifu-by-document-id', { documentId });

      return api
        .get(url)
        .then((response) => {
          const { data } = response.data || {};

          if (data) {
            this.entries = [data];

            return response;
          }

          throw new Error(
            'API failure during "electronic-instruction/apiFetchElectronicInstructionByDocumentId" request.',
          );
        })
        .finally(() => {
          this.runningRequests.apiFetchElectronicInstructionByDocumentId = false;
        });
    },

    /**
     * Fetches a brands from the backend.
     */
    apiFetchBrands() {
      this.runningRequests.apiFetchBrands = true;

      const url = api.getUrl('eifu-brand-entries');

      return api
        .get(url)
        .then((response) => {
          const { data } = response.data || {};

          if (data) {
            this.brands = [...data.brands];

            return response;
          }

          throw new Error('API failure during "electronic-instruction/apiFetchBrands" request.');
        })
        .finally(() => {
          this.runningRequests.apiFetchBrands = false;
        });
    },

    /**
     * Fetches the electronic instructions for a given reference number.
     */
    apiFetchElectronicInstructions({ query, type, exact }) {
      this.runningRequests.apiFetchElectronicInstructions = true;

      const url = api.getUrl('eifu-instructions', { query, type, exact });

      return api
        .get(url)
        .then((response) => {
          const { data } = response.data || {};

          if (Array.isArray(data)) {
            this.entries = data;

            return response;
          }

          throw new Error('API failure during "electronic-instruction/apiFetchElectronicInstructions" request.');
        })
        .finally(() => {
          this.runningRequests.apiFetchElectronicInstructions = false;
        });
    },

    /**
     * Fetches the history for an electronic instruction item.
     */
    apiFetchHistory(id) {
      this.runningRequests.apiFetchHistory = true;

      const url = api.getUrl('eifu-history', { id });

      return api
        .get(url)
        .then((response) => {
          const { data } = response.data || {};

          if (Array.isArray(data.history)) {
            this.history[id] = data.history;
          }

          if (Array.isArray(data.relations)) {
            this.relations[id] = data.relations;
          }

          if (Array.isArray(data.history) || Array.isArray(data.relations)) {
            return response;
          }

          throw new Error('API failure during "electronic-instruction/apiFetchHistory" request.');
        })
        .finally(() => {
          this.runningRequests.apiFetchHistory = false;
        });
    },

    /**
     * Requests a hard copy for an electronic instruction item.
     */
    apiRequestHardCopy({ fileId, validAsOf, brandNames, referenceNumbers, udiDiNumbers, formData }) {
      this.runningRequests.apiRequestHardCopy = true;

      const url = api.getUrl('eifu-request-hard-copy', { fileId });
      const requestData = {
        validAsOf,
        brandNames,
        referenceNumbers,
        udiDiNumbers,
        ...formData,
      };

      return api
        .post(url, requestData)
        .then((response) => {
          if (response.status === 204) {
            return response;
          }

          throw new Error('API failure during "electronic-instruction/apiRequestHardCopy" request.');
        })
        .finally(() => {
          this.runningRequests.apiRequestHardCopy = false;
        });
    },

    /**
     * Sends a problem report to Pimcore.
     */
    apiReportProblem(payload) {
      this.runningRequests.apiReportProblem = true;

      const url = api.getUrl('eifu-report-problem');

      return api.post(url, payload).finally(() => {
        this.runningRequests.apiReportProblem = false;
      });
    },

    /**
     * Sends a subscription demand to Pimcore.
     */
    apiSubscribeToAssetVersionUpdates({ assetId, formData }) {
      this.runningRequests.apiSubscribeToAssetVersionUpdates = true;

      const url = api.getUrl('eifu-subscribe', { assetId });

      return api.post(url, formData).finally(() => {
        this.runningRequests.apiSubscribeToAssetVersionUpdates = false;
      });
    },
  },
});
